import {
  computeDashboardKPIs,
  getTopCourses,
  computeLearnerIntelligence,
} from "./dashboardAnalyticsHelpers"
import { computeProgramPerformance } from "./programPerformanceHelpers"

// Turns numbers the dashboard already computes into plain-language sentences.
// Deliberately threshold-gated (a 2% wobble isn't "an insight") and capped at
// 4, prioritized negative > positive > neutral, so this reads like a human
// scanning the data rather than a dump of every metric that moved at all.
export const computeInsights = async (orgId, rangeKey = "30d") => {
  const [kpis, topCourses, programRows, learnerIntel] = await Promise.all([
    computeDashboardKPIs(orgId, rangeKey),
    getTopCourses(orgId, 5),
    computeProgramPerformance(orgId),
    computeLearnerIntelligence(orgId, rangeKey),
  ])

  const insights = []
  const add = (condition, insight) => {
    if (condition) insights.push(insight)
  }

  if (kpis.completionRate.change !== null) {
    const change = kpis.completionRate.change
    add(Math.abs(change) >= 3, {
      id: "completion-trend",
      tone: change >= 0 ? "positive" : "negative",
      icon: change >= 0 ? "TrendingUp" : "TrendingDown",
      text: `Completion rate ${change >= 0 ? "increased" : "dropped"} ${Math.abs(change)}% this period.`,
    })
  }

  if (kpis.revenue.hasAnyOrders && kpis.revenue.change !== null) {
    const change = kpis.revenue.change
    add(Math.abs(change) >= 5, {
      id: "revenue-trend",
      tone: change >= 0 ? "positive" : "negative",
      icon: change >= 0 ? "TrendingUp" : "TrendingDown",
      text: `Revenue ${change >= 0 ? "grew" : "declined"} ${Math.abs(change)}% compared to the previous period.`,
    })
  }

  if (kpis.credentials.change !== null) {
    const change = kpis.credentials.change
    add(Math.abs(change) >= 5, {
      id: "credentials-trend",
      tone: change >= 0 ? "positive" : "negative",
      icon: "Award",
      text: `Credentials issued ${change >= 0 ? "rose" : "fell"} ${Math.abs(change)}% this period.`,
    })
  }

  if (topCourses?.length && topCourses[0].enrollments > 0) {
    const top = topCourses[0]
    insights.push({
      id: "top-course",
      tone: "neutral",
      icon: "Trophy",
      text: `"${top.title}" is your top performing course — ${top.enrollments} enrollments, ${top.completionRate}% completion.`,
    })
  }

  const eligiblePrograms = programRows?.filter((p) => p.learners >= 5) || []
  if (eligiblePrograms.length >= 2) {
    const sorted = [...eligiblePrograms].sort((a, b) => b.completion - a.completion)
    const best = sorted[0]
    const worst = sorted[sorted.length - 1]
    const gap = best.completion - worst.completion
    add(gap >= 10, {
      id: "program-gap",
      tone: "neutral",
      icon: "GitCompare",
      text: `${best.name} is outperforming ${worst.name} by ${gap} points in completion rate.`,
    })
  }

  add(learnerIntel.dropout.rate >= 15, {
    id: "dropout-warning",
    tone: "negative",
    icon: "AlertTriangle",
    text: `${learnerIntel.dropout.rate}% of enrollments (${learnerIntel.dropout.count} learners) have stalled 30+ days — worth a re-engagement nudge.`,
  })

  add(learnerIntel.retention.rate > 0 && learnerIntel.retention.rate < 40, {
    id: "retention-warning",
    tone: "negative",
    icon: "UserMinus",
    text: `Only ${learnerIntel.retention.rate}% of previously active learners returned this period.`,
  })

  add(learnerIntel.retention.rate >= 70, {
    id: "retention-strong",
    tone: "positive",
    icon: "UserCheck",
    text: `${learnerIntel.retention.rate}% of previously active learners came back this period — strong retention.`,
  })

  if (insights.length === 0) {
    insights.push({
      id: "steady",
      tone: "neutral",
      icon: "Minus",
      text: "No major shifts this period — metrics are holding steady.",
    })
  }

  const priority = { negative: 0, positive: 1, neutral: 2 }
  return insights.sort((a, b) => priority[a.tone] - priority[b.tone]).slice(0, 4)
}